import { useEffect, useRef } from 'react'

/**
 * Fires `onRefresh()` when the browser tab becomes visible again AND the data
 * is older than `staleMs` milliseconds (default 5 min).
 *
 * Call `markRefreshed()` (returned from this hook) at the end of each successful
 * data fetch so the staleness clock resets.
 *
 * @param {Object} opts
 * @param {Function} opts.onRefresh  - called when tab is visible + data is stale
 * @param {number}  [opts.staleMs]   - staleness window in ms (default 300_000 = 5 min)
 * @returns {{ markRefreshed: Function }}
 */
export function useVisibilityRefresh({ onRefresh, staleMs = 300_000 }) {
    const lastRefreshedAt = useRef(Date.now())
    const onRefreshRef    = useRef(onRefresh)

    // Keep ref current without adding onRefresh to effect deps
    useEffect(() => { onRefreshRef.current = onRefresh }, [onRefresh])

    useEffect(() => {
        function handleVisibilityChange() {
            if (document.visibilityState !== 'visible') return
            if (Date.now() - lastRefreshedAt.current > staleMs) {
                onRefreshRef.current()
            }
        }

        document.addEventListener('visibilitychange', handleVisibilityChange)
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange)
    }, [staleMs])

    const markRefreshed = () => { lastRefreshedAt.current = Date.now() }

    return { markRefreshed }
}
